import { Navigate, Route, Routes } from "react-router-dom";
import PublicMenuLayout from "./layouts/PublicMenuLayout";
import Login from "./pages/Auth/Login";
import PublicMenu from "./pages/PublicMenu/PublicMenu";
import PublicOrderCheckout from "./pages/PublicMenu/PublicOrderCheckout";
import PublicCustomerOrders from "./pages/PublicMenu/PublicCustomerOrders";
import PublicCustomerOrderDetails from "./pages/PublicMenu/PublicCustomerOrderDetails";
import PublicProductDetails from "./pages/PublicMenu/PublicProductDetails";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import DashboardLayout from "./layouts/DashboardLayout";
import LegalPagesManager from "./pages/LegalPages/LegalPagesManager";
import FreeTrialRequestsManager from "./pages/FreeTrial/FreeTrialRequestsManager";
import FreeTrialRequestDetails from "./pages/FreeTrial/FreeTrialRequestDetails";
import Dashboard from "./pages/Dashboard";
import Products from "./pages/Products/Products";
import ProductForm from "./pages/Products/ProductForm";
import ProductDetails from "./pages/Products/ProductDetails";
import Categories from "./pages/Categories/Categories";
import CategoryForm from "./pages/Categories/CategoryForm";
import CategoryDetails from "./pages/Categories/CategoryDetails";
import Supplements from "./pages/Supplements/Supplements";
import SupplementForm from "./pages/Supplements/SupplementForm";
import Menus from "./pages/Menus/Menus";
import MenuForm from "./pages/Menus/MenuForm";
import MenuCustomize from "./pages/Menus/MenuCustomize";
import AiMenuDesigner from "./pages/Menus/AiMenuDesigner";
import MenuDetails from "./pages/Menus/MenuDetails";
import Orders from "./pages/Orders/Orders";
import OrderDetails from "./pages/Orders/OrderDetails";
import OrderForm from "./pages/Orders/OrderForm";
import MenuAnalytics from "./pages/Analytics/MenuAnalytics";
import ManagerActivityLogs from "./pages/Activity/ManagerActivityLogs";
import AccountSettings from "./pages/Account/AccountSettings";
import NotificationsPage from "./pages/Notifications/NotificationsPage";
import UserManagement from "./pages/Users/UserManagement";
import UserForm from "./pages/Users/UserForm";
import UserDetails from "./pages/Users/UserDetails";
import MenuImport from "./pages/MenuImport/MenuImport";
import MenuImportReview from "./pages/MenuImport/MenuImportReview";
import MenuImportSuccess from "./pages/MenuImport/MenuImportSuccess";
import PlatformFeatures from "./pages/PlatformFeatures/PlatformFeatures";
import Establishments from "./pages/Establishments/Establishments";
import EstablishmentForm from "./pages/Establishments/EstablishmentForm";
import EstablishmentDetails from "./pages/Establishments/EstablishmentDetails";
import QrPrintCatalog from "./pages/QrPrint/QrPrintCatalog";
import QrPrintProductForm from "./pages/QrPrint/QrPrintProductForm";
import QrPrintDesigner from "./pages/QrPrint/QrPrintDesigner";
import QrPrintAiCustomizer from "./pages/QrPrint/QrPrintAiCustomizer";
import QrPrintCart from "./pages/QrPrint/QrPrintCart";
import QrPrintOrders from "./pages/QrPrint/QrPrintOrders";
import QrPrintOrderDetails from "./pages/QrPrint/QrPrintOrderDetails";
import FieldSalesDashboard from "./pages/Marketing/FieldSalesDashboard";
import FieldSalesApprovalCenter from "./pages/Marketing/FieldSalesApprovalCenter";
import FieldSales from "./pages/Marketing/FieldSales";
import FieldSalesVisits from "./pages/Marketing/FieldSalesVisits";
import SalesRepresentatives from "./pages/Marketing/SalesRepresentatives";
import { useAuth } from "./contexts/AuthContext";
const MANAGER_ROLES = [
  "superadmin",
  "superadministrator",
  "manager",
  "restaurantmanager",
  "restaurantmanagerrole",
  "admin",
  "administrator",
];
const SUPER_ADMIN_ROLES = [
  "superadmin",
  "superadministrator",
];
const SALES_REPRESENTATIVE_ROLES = [
  "salesrepresentative",
  "salesrepresentatives",
  "marketing",
  "marketingmanager",
  "responsablemarketing",
];
const WAITER_ROLES = [
  "waiter",
  // Old role kept only to prevent access issues with existing users.
  "serveur",
];
const KITCHEN_ROLES = [
  "cuisini",
  // Old roles kept only to prevent access issues with existing users.
  "cuisine",
  "kitchen",
  "cuisiner",
  "cuisinier",
];
const ORDERS_ONLY_ROLES = [
  ...WAITER_ROLES,
  ...KITCHEN_ROLES,
];
// Normalizes role values so different backend formats can be compared safely.
const normalizeRole = (role) => {
  return String(role || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, "");
};
// Extracts a usable role string from different API response structures.
const extractRoleValue = (role) => {
  if (!role) return "";
  if (
    typeof role === "string" ||
    typeof role === "number"
  ) {
    return role;
  }
  if (typeof role === "object") {
    return (
      role.name ||
      role.slug ||
      role.code ||
      role.value ||
      role.key ||
      role.role ||
      ""
    );
  }
  return "";
};
// Finds the current user's role regardless of the backend response structure.
const getCurrentUserRole = (user) => {
  const possibleRoles = [
    user?.role?.name,
    user?.role?.slug,
    user?.role?.code,
    user?.role?.value,
    user?.role,
    user?.data?.role?.name,
    user?.data?.role?.slug,
    user?.data?.role?.code,
    user?.data?.role?.value,
    user?.data?.role,
    user?.user?.role?.name,
    user?.user?.role?.slug,
    user?.user?.role?.code,
    user?.user?.role?.value,
    user?.user?.role,
    user?.account?.role?.name,
    user?.account?.role,
    user?.profile?.role?.name,
    user?.profile?.role,
    user?.roles?.[0]?.name,
    user?.roles?.[0]?.slug,
    user?.roles?.[0]?.code,
    user?.roles?.[0]?.value,
    user?.roles?.[0],
  ];
  for (const possibleRole of possibleRoles) {
    const roleValue =
      extractRoleValue(possibleRole);
    if (roleValue) {
      return roleValue;
    }
  }
  return "";
};
// Checks whether the current role can access manager pages.
const isManagerRole = (role) => {
  return MANAGER_ROLES.includes(
    normalizeRole(role),
  );
};
// Checks whether the current role is a Super Admin.
const isSuperAdminRole = (role) => {
  return SUPER_ADMIN_ROLES.includes(
    normalizeRole(role),
  );
};
// Checks whether the role belongs to a field-sales representative.
const isSalesRepresentativeRole = (role) => {
  return SALES_REPRESENTATIVE_ROLES.includes(normalizeRole(role));
};
// Checks whether the current role is a waiter.
const isWaiterRole = (role) => {
  return WAITER_ROLES.includes(
    normalizeRole(role),
  );
};
// Checks whether the role should only work with orders.
const isOrdersOnlyRole = (role) => {
  return ORDERS_ONLY_ROLES.includes(
    normalizeRole(role),
  );
};
// Redirects authenticated users to the correct home page for their role.
function PrivateHomeRedirect() {
  const { user } = useAuth();
  const role =
    getCurrentUserRole(user);
  if (!role) {
    return null;
  }
  if (isOrdersOnlyRole(role)) {
    return (
      <Navigate
        to="/orders"
        replace
      />
    );
  }
  if (isSalesRepresentativeRole(role)) {
    return <Navigate to="/marketing/dashboard" replace />;
  }
  if (isManagerRole(role)) {
    return (
      <Navigate
        to="/dashboard"
        replace
      />
    );
  }
  return (
    <Navigate
      to="/account-settings"
      replace
    />
  );
}
// Protects pages that should only be available to managers.
function ManagerOnly({ children }) {
  const { user } = useAuth();
  const role =
    getCurrentUserRole(user);
  if (!role) {
    return null;
  }
  if (isManagerRole(role)) {
    return children;
  }
  if (isOrdersOnlyRole(role)) {
    return (
      <Navigate
        to="/orders"
        replace
      />
    );
  }
  return (
    <Navigate
      to="/account-settings"
      replace
    />
  );
}
// Protects pages that should only be available to Super Admins.
function SuperAdminOnly({ children }) {
  const { user } = useAuth();
  const role =
    getCurrentUserRole(user);
  if (!role) {
    return null;
  }
  if (isSuperAdminRole(role)) {
    return children;
  }
  if (isOrdersOnlyRole(role)) {
    return (
      <Navigate
        to="/orders"
        replace
      />
    );
  }
  return (
    <Navigate
      to="/dashboard"
      replace
    />
  );
}
// Allows a sales representative to access field-sales pages.
// Super Admins may also inspect these pages.
function SalesRepresentativeOnly({ children }) {
  const { user } = useAuth();
  const role = getCurrentUserRole(user);
  if (!role) return null;
  if (isSalesRepresentativeRole(role) || isSuperAdminRole(role)) {
    return children;
  }
  return <Navigate to={isOrdersOnlyRole(role) ? "/orders" : "/dashboard"} replace />;
}
// Controls access to the AI menu designer feature.
function AiMenuDesignAccessOnly({
  children,
}) {
  const {
    user,
    getFeature,
  } = useAuth();
  const role =
    getCurrentUserRole(user);
  if (!role) {
    return null;
  }
  if (!isManagerRole(role)) {
    if (isOrdersOnlyRole(role)) {
      return (
        <Navigate
          to="/orders"
          replace
        />
      );
    }
    return (
      <Navigate
        to="/account-settings"
        replace
      />
    );
  }
  /*
   \\\\\\\\* Super Admin can enter the page directly.
   \\\\\\\\* Restaurant managers depend on the effective
   \\\\\\\\* feature access returned by Laravel.
   */
  if (isSuperAdminRole(role)) {
    return children;
  }
  const feature =
    getFeature("ai_menu_design");
  /*
   \\\\\\\\* If Laravel hides or disables the feature,
   \\\\\\\\* do not expose the page through a direct URL.
   */
  if (
    !feature ||
    feature.visible !== true ||
    feature.available !== true ||
    feature.locked === true
  ) {
    return (
      <Navigate
        to="/dashboard"
        replace
      />
    );
  }
  return children;
}
// Controls access to the AI menu-import feature.
function MenuImportAccessOnly({
  children,
}) {
  const {
    user,
    getFeature,
  } = useAuth();
  const role =
    getCurrentUserRole(user);
  if (!role) {
    return null;
  }
  if (!isManagerRole(role)) {
    if (isOrdersOnlyRole(role)) {
      return (
        <Navigate
          to="/orders"
          replace
        />
      );
    }
    return (
      <Navigate
        to="/account-settings"
        replace
      />
    );
  }
  /*
   \\\\\\\\* Super Admin uses the feature-management page.
   \\\\\\\\* Restaurant managers use AI Menu Import according to
   \\\\\\\\* the effective access returned by Laravel.
   */
  if (isSuperAdminRole(role)) {
    return children;
  }
  const feature =
    getFeature("ai_menu_import");
  /*
   \\\\\\\\* No feature returned means Laravel intentionally
   \\\\\\\\* hides the existence of AI Menu Import.
   */
  if (!feature || feature.visible !== true) {
    return (
      <Navigate
        to="/dashboard"
        replace
      />
    );
  }
  /*
   \\\\\\\\* Individually disabled users can see the locked
   \\\\\\\\* sidebar entry, but they cannot enter the page
   \\\\\\\\* or bypass the lock by typing the URL manually.
   */
  if (
    feature.locked === true ||
    feature.available !== true
  ) {
    return (
      <Navigate
        to="/dashboard"
        replace
      />
    );
  }
  return children;
}
// Controls access to the AI QR product designer feature.
function QrPrintAiDesignAccessOnly({
  children,
}) {
  const {
    user,
    getFeature,
  } = useAuth();
  const role =
    getCurrentUserRole(user);
  if (!role) {
    return null;
  }
  if (!isManagerRole(role)) {
    if (isOrdersOnlyRole(role)) {
      return (
        <Navigate
          to="/orders"
          replace
        />
      );
    }
    return (
      <Navigate
        to="/account-settings"
        replace
      />
    );
  }
  if (isSuperAdminRole(role)) {
    return children;
  }
  const feature =
    getFeature(
      "qr_print_ai_design",
    );
  if (
    !feature ||
    feature.visible !== true
  ) {
    return (
      <Navigate
        to="/qr-print/products"
        replace
      />
    );
  }
  if (
    feature.available !== true ||
    feature.locked === true
  ) {
    return (
      <Navigate
        to="/qr-print/products"
        replace
      />
    );
  }
  return children;
}
// Allows managers, waiters, and kitchen staff to access orders.
function OrdersAccessOnly({
  children,
}) {
  const { user } = useAuth();
  const role =
    getCurrentUserRole(user);
  if (!role) {
    return null;
  }
  if (
    isManagerRole(role) ||
    isOrdersOnlyRole(role)
  ) {
    return children;
  }
  return (
    <Navigate
      to="/account-settings"
      replace
    />
  );
}
// Allows managers and waiters to create new orders.
function OrderCreateAccessOnly({
  children,
}) {
  const { user } = useAuth();
  const role =
    getCurrentUserRole(user);
  if (!role) {
    return null;
  }
  if (
    isManagerRole(role) ||
    isWaiterRole(role)
  ) {
    return children;
  }
  if (isOrdersOnlyRole(role)) {
    return (
      <Navigate
        to="/orders"
        replace
      />
    );
  }
  return (
    <Navigate
      to="/account-settings"
      replace
    />
  );
}
function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/:establishmentSlug/:menuSlug" element={<PublicMenuLayout />}>
        <Route index element={<PublicMenu />} />
        <Route path="products/:productId" element={<PublicProductDetails />} />
        <Route path="order" element={<PublicOrderCheckout />} />
        <Route path="orders" element={<PublicCustomerOrders />} />
        <Route path="orders/:orderId" element={<PublicCustomerOrderDetails />} />
      </Route>
      {/* Authenticated application */}
      <Route element={<ProtectedRoute />}>
        <Route
          path="/home"
          element={<PrivateHomeRedirect />}
        />
        <Route
          path="/menus/:menuId/customize"
          element={
            <ManagerOnly>
              <MenuCustomize />
            </ManagerOnly>
          }
        />
        <Route
          path="/menus/:menuId/ai-design"
          element={
            <ManagerOnly>
              <AiMenuDesignAccessOnly>
                <AiMenuDesigner />
              </AiMenuDesignAccessOnly>
            </ManagerOnly>
          }
        />
        <Route element={<DashboardLayout />}>
          <Route
            path="/dashboard"
            element={
              <ManagerOnly>
                <Dashboard />
              </ManagerOnly>
            }
          />
          <Route
            path="/free-trial-requests"
            element={
              <SuperAdminOnly>
                <FreeTrialRequestsManager />
              </SuperAdminOnly>
            }
          />
          <Route
            path="/free-trial-requests/:id"
            element={
              <SuperAdminOnly>
                <FreeTrialRequestDetails />
              </SuperAdminOnly>
            }
          />
          <Route
            path="/legal-pages"
            element={
              <SuperAdminOnly>
                <LegalPagesManager />
              </SuperAdminOnly>
            }
          />
          {/* Super Admin platform features */}
          <Route
            path="/platform-features"
            element={
              <SuperAdminOnly>
                <PlatformFeatures />
              </SuperAdminOnly>
            }
          />
          <Route
            path="/establishments"
            element={
              <SuperAdminOnly>
                <Establishments />
              </SuperAdminOnly>
            }
          />
          <Route
            path="/establishments/new"
            element={
              <SuperAdminOnly>
                <EstablishmentForm />
              </SuperAdminOnly>
            }
          />
          <Route
            path="/establishments/:establishmentId"
            element={
              <SuperAdminOnly>
                <EstablishmentDetails />
              </SuperAdminOnly>
            }
          />
          <Route
            path="/establishments/:establishmentId/edit"
            element={
              <SuperAdminOnly>
                <EstablishmentForm />
              </SuperAdminOnly>
            }
          />
          {/* Super Admin sales representatives management */}
          <Route
            path="/sales-representatives"
            element={<SuperAdminOnly><SalesRepresentatives /></SuperAdminOnly>}
          />
          <Route
            path="/sales-representatives/new"
            element={<SuperAdminOnly><UserForm /></SuperAdminOnly>}
          />
          <Route
            path="/sales-representatives/:userId/edit"
            element={<SuperAdminOnly><UserForm /></SuperAdminOnly>}
          />
          {/* Field sales: restaurant registrations and visits */}
          <Route
            path="/marketing/dashboard"
            element={<SalesRepresentativeOnly><FieldSalesDashboard /></SalesRepresentativeOnly>}
          />
          {/* Super Admin sales verification — three standalone pages */}
          <Route path="/admin/sales-verification" element={<SuperAdminOnly><FieldSalesApprovalCenter view="overview" /></SuperAdminOnly>} />
          <Route path="/admin/sales-verification/subscriptions" element={<SuperAdminOnly><FieldSalesApprovalCenter view="subscriptions" /></SuperAdminOnly>} />
          <Route path="/admin/sales-verification/orders" element={<SuperAdminOnly><FieldSalesApprovalCenter view="orders" /></SuperAdminOnly>} />
          <Route
            path="/marketing/field-visits/new"
            element={<SalesRepresentativeOnly><FieldSales /></SalesRepresentativeOnly>}
          />
          <Route
            path="/marketing/visits"
            element={<SalesRepresentativeOnly><FieldSalesVisits /></SalesRepresentativeOnly>}
          />
          <Route
            path="/users"
            element={
              <ManagerOnly>
                <UserManagement />
              </ManagerOnly>
            }
          />
          <Route
            path="/users/new"
            element={
              <ManagerOnly>
                <UserForm />
              </ManagerOnly>
            }
          />
          <Route
            path="/users/:userId"
            element={
              <ManagerOnly>
                <UserDetails />
              </ManagerOnly>
            }
          />
          <Route
            path="/users/:userId/edit"
            element={
              <ManagerOnly>
                <UserForm />
              </ManagerOnly>
            }
          />
          {/* AI Menu Import */}
          <Route
            path="/menu-import"
            element={
              <MenuImportAccessOnly>
                <MenuImport />
              </MenuImportAccessOnly>
            }
          />
          <Route
            path="/menu-imports/:importId/review"
            element={
              <MenuImportAccessOnly>
                <MenuImportReview />
              </MenuImportAccessOnly>
            }
          />
          <Route
            path="/menu-imports/:importId/success"
            element={
              <MenuImportAccessOnly>
                <MenuImportSuccess />
              </MenuImportAccessOnly>
            }
          />
          <Route
            path="/products"
            element={
              <ManagerOnly>
                <Products />
              </ManagerOnly>
            }
          />
          <Route
            path="/products/new"
            element={
              <ManagerOnly>
                <ProductForm />
              </ManagerOnly>
            }
          />
          <Route
            path="/products/:productId"
            element={
              <ManagerOnly>
                <ProductDetails />
              </ManagerOnly>
            }
          />
          <Route
            path="/products/:productId/edit"
            element={
              <ManagerOnly>
                <ProductForm />
              </ManagerOnly>
            }
          />
          <Route
            path="/categories"
            element={
              <ManagerOnly>
                <Categories />
              </ManagerOnly>
            }
          />
          <Route
            path="/categories/new"
            element={
              <ManagerOnly>
                <CategoryForm />
              </ManagerOnly>
            }
          />
          <Route
            path="/categories/:categoryId"
            element={
              <ManagerOnly>
                <CategoryDetails />
              </ManagerOnly>
            }
          />
          <Route
            path="/categories/:categoryId/edit"
            element={
              <ManagerOnly>
                <CategoryForm />
              </ManagerOnly>
            }
          />
          <Route
            path="/supplements"
            element={
              <ManagerOnly>
                <Supplements />
              </ManagerOnly>
            }
          />
          <Route
            path="/supplements/new"
            element={
              <ManagerOnly>
                <SupplementForm />
              </ManagerOnly>
            }
          />
          <Route
            path="/supplements/:supplementId/edit"
            element={
              <ManagerOnly>
                <SupplementForm />
              </ManagerOnly>
            }
          />
          <Route
            path="/menus"
            element={
              <ManagerOnly>
                <Menus />
              </ManagerOnly>
            }
          />
          <Route
            path="/menus/new"
            element={
              <ManagerOnly>
                <MenuForm />
              </ManagerOnly>
            }
          />
          <Route
            path="/menus/:menuId"
            element={
              <ManagerOnly>
                <MenuDetails />
              </ManagerOnly>
            }
          />
          <Route
            path="/menus/:menuId/edit"
            element={
              <ManagerOnly>
                <MenuForm />
              </ManagerOnly>
            }
          />
          {/* QR Print products */}
          <Route
            path="/qr-print/products"
            element={
              <ManagerOnly>
                <QrPrintCatalog />
              </ManagerOnly>
            }
          />
          <Route
            path="/qr-print/products/:productId/customize"
            element={
              <ManagerOnly>
                <QrPrintDesigner />
              </ManagerOnly>
            }
          />
          <Route
            path="/qr-print/products/:productId/ai-customize"
            element={
              <ManagerOnly>
                <QrPrintAiDesignAccessOnly>
                  <QrPrintAiCustomizer />
                </QrPrintAiDesignAccessOnly>
              </ManagerOnly>
            }
          />
          <Route
            path="/qr-print/cart"
            element={
              <ManagerOnly>
                <QrPrintCart />
              </ManagerOnly>
            }
          />
          <Route
            path="/qr-print/orders"
            element={
              <ManagerOnly>
                <QrPrintOrders />
              </ManagerOnly>
            }
          />
          <Route
            path="/qr-print/orders/:orderId"
            element={
              <ManagerOnly>
                <QrPrintOrderDetails />
              </ManagerOnly>
            }
          />
          <Route
            path="/qr-print/admin/products/new"
            element={
              <SuperAdminOnly>
                <QrPrintProductForm />
              </SuperAdminOnly>
            }
          />
          <Route
            path="/qr-print/admin/products/:productId/edit"
            element={
              <SuperAdminOnly>
                <QrPrintProductForm />
              </SuperAdminOnly>
            }
          />
          <Route
            path="/account-settings"
            element={
              <AccountSettings />
            }
          />
          <Route
            path="/notifications"
            element={<NotificationsPage />}
          />
          <Route
            path="/orders"
            element={
              <OrdersAccessOnly>
                <Orders />
              </OrdersAccessOnly>
            }
          />
          <Route
            path="/orders/new"
            element={
              <OrderCreateAccessOnly>
                <OrderForm />
              </OrderCreateAccessOnly>
            }
          />
          <Route
            path="/orders/:orderId"
            element={
              <OrdersAccessOnly>
                <OrderDetails />
              </OrdersAccessOnly>
            }
          />
          <Route
            path="/menu-analytics"
            element={
              <ManagerOnly>
                <MenuAnalytics />
              </ManagerOnly>
            }
          />
          <Route
            path="/activity-tracking"
            element={
              <ManagerOnly>
                <ManagerActivityLogs />
              </ManagerOnly>
            }
          />
        </Route>
      </Route>
      <Route
        path="*"
        element={
          <Navigate
            to="/login"
            replace
          />
        }
      />
    </Routes>
  );
}
export default App;
